import React, { FC, useMemo } from 'react';
import { ICertificate } from '../custom-general/custom-certificates/form';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import AnomalyRecommendation from './anomaly-recommendation';
import Conditional from '../../common/conditional';
import CustomClass from '../custom-general/custom-damage-class';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomPDFExport from '../../blocks/pdf-export';
import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import DamageClassSummary from './damage-class-summary';
import ExecutiveSummary from './executive-summary';
import OverviewSheet from '../custom-general/overview-sheet';
import StructureAnalytics from './report-items/structureAnalytics';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface FVIStructureReportProps {
	hasPDO: string;
	certificates?: ICertificate;
	coverData: models.CustomFVIData;
	projectDetails: models.Project;
	structureItems: models.FVIStructureItem[];
	reportTitle: string;
	checklistItems: models.ChecklistItem[];
	pdoChecklistItems: models.FVIPDOChecklistItem[];
	additionalComment: string;
	executiveSummary: string;
	pidData: models.PIDData;
	includeSummary: models.IncludeSummary;
	generalPhotos: models.PDFImageProps[];
	anomalyLocationData: models.AnomalyLocationData;
}

const FVIStructureReport: FC<FVIStructureReportProps> = (props) => {
	const { inspectionTypeName } = props.projectDetails;
	
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure (Structure).',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const executiveSummary = useMemo(() => {
		if (props.includeSummary === 'include') {
			return props.executiveSummary + utils.getExecutiveSummaryAffix(props.structureItems, 'structure member(s)');
		}

		return props.executiveSummary;
	}, [props.structureItems, props.includeSummary, props.executiveSummary]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<CustomClass />

			<ExecutiveSummary summary={executiveSummary} />

			{props.certificates && <CustomCertificatePdf certificates={props.certificates} />}

			<DamageClassSummary
				items={props.structureItems}
				itemNumberKey='structureMemberName'
				columns={damageClassColumns}
			/>
	
			<CustomPDFExport>
				{(params) => (
					<AnomalyRecommendation
						projectDetails={props.projectDetails} 
						items={props.structureItems}
						exportParams={params}
						title='Summary'
						showSummarySection={false}
					/>
				)}
			</CustomPDFExport>

			<StructureAnalytics 
				includePDO={utils.matchPhrases(props.hasPDO, 'yes')}
				includeEmptyPDONotice
				pdoChecklist={props.pdoChecklistItems}
				pidData={props.pidData}
				anomalyLocationData={props.anomalyLocationData}
				checklistItems={props.checklistItems} 
				additionalComment={props.additionalComment} 
				items={props.structureItems} 
			/>

			<Conditional isVisible={props.generalPhotos && props.generalPhotos.length > 0}>
				<CustomGeneralPhotos label="General Photos" photos={props.generalPhotos} />
			</Conditional>
		</React.Fragment>
	);
};

const damageClassColumns: models.Column[] = [
	{
		path: 'structureMemberName',
		label: 'Structure Member Name',
	},
];

export default withPDF(FVIStructureReport);
